/**
 * Purchase Form Page
 *
 * Create/edit purchase invoices including line items, totals preview,
 * invoice attachment and optional immediate receive of all lines.
 *
 * @module pages/purchases/PurchaseForm
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Table,
  Spinner,
  Alert,
  InputGroup,
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import {
  createPurchase,
  getPurchase,
  updatePurchase,
  addPurchaseItem,
  updatePurchaseItem,
  deletePurchaseItem,
  receiveFullPurchase,
  PURCHASE_STATUS,
  PURCHASE_STATUS_LABELS,
  isPurchaseEditable,
} from '../../services/purchaseService';
import { createPurchaseOrder } from '../../services/purchaseOrderService';
import { getActiveSuppliers } from '../../services/supplierService';
import { listInventory } from '../../services/inventoryService';
import { listProjects } from '../../services/projectService';
import { toast } from '../../utils/toast';
import { formatCurrency } from '../../utils/formatters';
import PurchaseLineItem from '../../components/purchases/PurchaseLineItem';
import InvoiceUploader from '../../components/purchases/InvoiceUploader';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './PurchaseForm.css';

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Line item as a CARD, for phones.
 *
 * Contraparte movil de <PurchaseLineItem> (que es un <tr>). El repetidor de
 * lineas metia 6 inputs por fila dentro de una <Table> de minWidth 900px: en
 * un telefono de 375px eso es scroll horizontal dentro de scroll vertical, o
 * sea dar de alta una compra era imposible en la mano. El patron de
 * tabla->cards ya estaba resuelto en QuotationBuilder (QuotationBuilder.css
 * 22-41 + QuotationItemCard.js); esto es ese patron, no uno nuevo.
 *
 * Comparte handlers (onChange/onRemove) con la fila: misma firma, mismo
 * estado. No hay una segunda fuente de verdad.
 */
const PurchaseLineCard = ({
  item,
  index,
  inventoryOptions,
  currency,
  onChange,
  onRemove,
  disabled,
}) => {
  const qty = Number(item.quantity_ordered || 0);
  const cost = Number(item.unit_cost || 0);
  const lineTotal = qty * cost;
  const update = (field, value) => onChange && onChange(index, { [field]: value });

  return (
    <Card className="purchase-line-card">
      <Card.Body className="p-3">
        <div className="purchase-line-card-header mb-3">
          <span className="badge bg-light text-dark purchase-line-card-number">
            Linea #{index + 1}
          </span>
          <Button
            variant="outline-danger"
            className="purchase-line-card-remove"
            onClick={() => onRemove && onRemove(index)}
            disabled={disabled}
            aria-label={`Eliminar linea ${index + 1}`}
          >
            <i className="bi bi-trash"></i>
          </Button>
        </div>

        <Form.Group className="mb-3">
          <Form.Label className="purchase-line-card-label">Producto</Form.Label>
          <Form.Select
            value={item.inventory_id || ''}
            onChange={(e) => update('inventory_id', e.target.value)}
            disabled={disabled}
            aria-label={`Producto linea ${index + 1}`}
          >
            <option value="">Seleccionar producto...</option>
            {inventoryOptions.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.sku ? `${inv.sku} - ` : ''}
                {inv.name}
                {inv.unit ? ` (${inv.unit})` : ''}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Row className="mb-3">
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="purchase-line-card-label">Cantidad</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={item.quantity_ordered ?? ''}
                onChange={(e) =>
                  update('quantity_ordered', e.target.value === '' ? '' : Number(e.target.value))
                }
                disabled={disabled}
                placeholder="0"
                aria-label={`Cantidad linea ${index + 1}`}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="purchase-line-card-label">Costo unit.</Form.Label>
              <InputGroup>
                <InputGroup.Text className="px-2">{currency}</InputGroup.Text>
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.unit_cost ?? ''}
                  onChange={(e) =>
                    update('unit_cost', e.target.value === '' ? '' : Number(e.target.value))
                  }
                  disabled={disabled}
                  placeholder="0.00"
                  aria-label={`Costo unitario linea ${index + 1}`}
                />
              </InputGroup>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="purchase-line-card-label">Lote</Form.Label>
              <Form.Control
                type="text"
                value={item.lot_number || ''}
                onChange={(e) => update('lot_number', e.target.value)}
                disabled={disabled}
                placeholder="Opcional"
                maxLength={50}
                aria-label={`Lote linea ${index + 1}`}
              />
            </Form.Group>
          </Col>
          <Col xs={6}>
            <Form.Group>
              <Form.Label className="purchase-line-card-label">Vence</Form.Label>
              <Form.Control
                type="date"
                value={item.expiration_date || ''}
                onChange={(e) => update('expiration_date', e.target.value)}
                disabled={disabled}
                aria-label={`Vencimiento linea ${index + 1}`}
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label className="purchase-line-card-label">Notas</Form.Label>
          <Form.Control
            type="text"
            value={item.notes || ''}
            onChange={(e) => update('notes', e.target.value)}
            disabled={disabled}
            placeholder="Opcional"
            maxLength={200}
            aria-label={`Notas linea ${index + 1}`}
          />
        </Form.Group>

        <div className="d-flex justify-content-between align-items-center pt-2 border-top purchase-line-card-total">
          <span className="text-muted">Total</span>
          <strong>{formatCurrency(lineTotal, currency)}</strong>
        </div>
      </Card.Body>
    </Card>
  );
};

const blankLine = () => ({
  __key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null, // existing item id (when editing)
  inventory_id: '',
  quantity_ordered: '',
  unit_cost: '',
  lot_number: '',
  expiration_date: '',
  notes: '',
  // Track original state for diff on edit
  _original: null,
});

const PurchaseForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  const { isAdmin } = useAuth();

  const [header, setHeader] = useState({
    supplier_id: '',
    project_id: '',
    invoice_number: '',
    invoice_date: today(),
    location: '',
    currency: 'CRC',
    exchange_rate: 1,
    notes: '',
    tax_amount: 0,
    attachment_url: '',
    // Flow A nace 'invoiced'. Antes acá decía PURCHASE_STATUS.PENDING
    // ('pending'), valor que el CHECK de `purchases` no admite: crear una
    // compra desde esta pantalla reventaba contra la base.
    status: PURCHASE_STATUS.INVOICED,
  });
  const [lines, setLines] = useState([blankLine()]);
  const [removedLineIds, setRemovedLineIds] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // 'invoice' = Flow A (createPurchase with invoice_date)
  // 'po'      = Flow B (createPurchaseOrder, no invoice yet — #130)
  const [flow, setFlow] = useState('invoice');

  // Active suppliers
  const { data: suppliersResponse } = useQuery({
    queryKey: ['active-suppliers'],
    queryFn: getActiveSuppliers,
    staleTime: 1000 * 60 * 5,
  });
  const suppliers = suppliersResponse?.data || [];

  // Inventory items (active only)
  const { data: inventoryResponse } = useQuery({
    queryKey: ['inventory-active-list'],
    // #522 — `includeCosts` es opt-in desde que el catálogo dejó de mandar
    // `unit_cost`/`sale_price` por defecto. Acá hace falta: el formulario
    // precarga el costo de cada línea de compra. Es admin-only por `RoleGuard`.
    queryFn: () => listInventory({ pageSize: 500, includeCosts: true }),
    staleTime: 1000 * 60 * 5,
  });
  const inventoryOptions = useMemo(
    () => (inventoryResponse?.data || []).filter((i) => i.is_active !== false),
    [inventoryResponse]
  );

  // Proyectos, para poder atribuir la PO. El motivo ORIGINAL de este campo era
  // la aprobación delegada por proyecto (sin `project_id` la policy
  // `purchases_delegated_leader_approve` no aplicaba y sólo un admin podía
  // aprobar, en silencio). Desde #550 aprueba el admin siempre, así que ese
  // motivo desapareció — pero el campo se queda: atribuir la compra a un
  // proyecto es lo que hace que el gasto llegue al presupuesto y a los
  // budget_alerts. Antes este formulario ni siquiera preguntaba por el
  // proyecto y toda PO creada desde la web nacía huérfana.
  const { data: projectsResponse } = useQuery({
    queryKey: ['projects-for-purchase'],
    queryFn: () => listProjects({ pageSize: 200 }),
    staleTime: 1000 * 60 * 5,
  });
  const projectOptions = projectsResponse?.data || [];

  // Existing purchase (edit mode)
  const {
    data: purchaseResponse,
    isLoading: loadingPurchase,
    error: loadError,
  } = useQuery({
    queryKey: ['purchase', id],
    queryFn: () => getPurchase(id),
    enabled: isEdit,
  });

  const existingPurchase = purchaseResponse?.data;

  // Hydrate state when loading existing purchase
  useEffect(() => {
    if (!isEdit || !existingPurchase) return;
    setHeader({
      supplier_id: existingPurchase.supplier_id || '',
      project_id: existingPurchase.project_id || '',
      invoice_number: existingPurchase.invoice_number || '',
      invoice_date: existingPurchase.invoice_date || today(),
      location: existingPurchase.location || '',
      currency: existingPurchase.currency || 'CRC',
      exchange_rate: existingPurchase.exchange_rate || 1,
      notes: existingPurchase.notes || '',
      tax_amount: existingPurchase.tax_amount || 0,
      attachment_url: existingPurchase.attachment_url || '',
      status: existingPurchase.status || PURCHASE_STATUS.INVOICED,
    });

    const items = existingPurchase.items || [];
    if (items.length === 0) {
      setLines([blankLine()]);
    } else {
      setLines(
        items.map((it) => ({
          __key: it.id,
          id: it.id,
          inventory_id: it.inventory_id || '',
          quantity_ordered: it.quantity_ordered ?? '',
          unit_cost: it.unit_cost ?? '',
          lot_number: it.lot_number || '',
          expiration_date: it.expiration_date || '',
          notes: it.notes || '',
          _original: {
            inventory_id: it.inventory_id || '',
            quantity_ordered: it.quantity_ordered ?? '',
            unit_cost: it.unit_cost ?? '',
            lot_number: it.lot_number || '',
            expiration_date: it.expiration_date || '',
            notes: it.notes || '',
          },
        }))
      );
    }
  }, [existingPurchase, isEdit]);

  // Editable en los estados iniciales de LOS DOS flujos: 'invoiced' (Flujo A,
  // factura en mano) y 'draft' (Flujo B, el borrador recién creado). La
  // condición anterior era `status !== 'invoiced'`, o sea que un borrador de
  // PO — el caso que MÁS se edita — se abría con el formulario entero
  // deshabilitado. Ver EDITABLE_PURCHASE_STATUSES en purchaseService.
  const isLockedForEdit = isEdit && existingPurchase && !isPurchaseEditable(existingPurchase.status);

  // Los requisitos de factura son del estado 'invoiced', no del formulario:
  // editando un borrador de Flujo B todavía no hay factura que exigir.
  const requiresInvoiceData = isEdit
    ? existingPurchase?.status === PURCHASE_STATUS.INVOICED
    : flow === 'invoice';

  // Totals preview
  const subtotal = useMemo(
    () =>
      lines.reduce((acc, l) => {
        const q = Number(l.quantity_ordered || 0);
        const c = Number(l.unit_cost || 0);
        return acc + q * c;
      }, 0),
    [lines]
  );
  const tax = Number(header.tax_amount || 0);
  const total = subtotal + tax;
  const totalCRC =
    header.currency === 'USD'
      ? total * Number(header.exchange_rate || 0)
      : null;

  const handleHeaderChange = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineChange = (index, partial) => {
    setLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...partial };
      return next;
    });
  };

  const handleAddLine = () => setLines((prev) => [...prev, blankLine()]);

  const handleRemoveLine = (index) => {
    setLines((prev) => {
      const removed = prev[index];
      if (removed?.id) {
        setRemovedLineIds((ids) => [...ids, removed.id]);
      }
      const next = prev.filter((_, i) => i !== index);
      return next.length > 0 ? next : [blankLine()];
    });
  };

  const validate = () => {
    if (!header.supplier_id) {
      return 'Seleccioná un proveedor';
    }
    if (!header.invoice_date) {
      return 'La fecha de factura es requerida';
    }
    // Espejo del CHECK `purchase_invoice_required_when_invoiced`
    // (status <> 'invoiced' OR (invoice_number IS NOT NULL AND invoice_date IS
    // NOT NULL)). El Flujo A crea la compra directamente en 'invoiced', así
    // que sin número de factura el INSERT rebota con el nombre crudo del
    // constraint DESPUÉS de que el usuario llenó todas las líneas.
    if (requiresInvoiceData && !header.invoice_number?.trim()) {
      return 'Falta el número de factura: es obligatorio para registrar una compra facturada';
    }
    // Comprobante obligatorio (Jorge, 2026-07-28: "va a subir el
    // comprobante al app"). Solo en el flujo A — el flujo B crea una PO
    // ANTES de comprar, cuando todavía no existe factura que fotografiar.
    //
    // Esto es un espejo del constraint de la base
    // (`purchase_receipt_required_when_invoiced`), no el gate real: sirve
    // para avisar antes de que el usuario llene 8 líneas y pierda el
    // trabajo contra un error de Postgres.
    if (requiresInvoiceData && !header.attachment_url) {
      return 'Falta el comprobante: tomale una foto a la factura o subí el PDF';
    }
    if (header.currency === 'USD') {
      const rate = Number(header.exchange_rate);
      if (!rate || rate <= 0) {
        return 'El tipo de cambio es requerido para USD';
      }
    }
    const validLines = lines.filter((l) => l.inventory_id);
    if (validLines.length === 0) {
      return 'Agregá al menos una línea';
    }
    for (const l of validLines) {
      const q = Number(l.quantity_ordered);
      const c = Number(l.unit_cost);
      if (!q || q <= 0) {
        return 'Todas las líneas deben tener cantidad mayor a 0';
      }
      if (c < 0 || Number.isNaN(c)) {
        return 'Costo unitario inválido';
      }
    }
    return null;
  };

  // OJO: `status` NO va acá a propósito. `updatePurchase` ya no acepta
  // ediciones de estado (paridad con el carril MCP, que las rechaza desde
  // siempre) — el estado lo mueve el flujo de aprobación, no un campo del
  // formulario. En la creación, `createPurchase` pone el estado inicial.
  const buildHeaderPayload = () => ({
    supplier_id: header.supplier_id,
    // L10 — el select "Proyecto" existe y se muestra en LOS DOS flujos, pero
    // este payload lo descartaba: en el flujo de factura (Flow A) el usuario
    // elegía el proyecto y la compra nacía igual con project_id NULL, invisible
    // para el costo del proyecto. El flujo de PO sí lo mandaba (línea ~503).
    project_id: header.project_id || null,
    invoice_number: header.invoice_number || null,
    invoice_date: header.invoice_date,
    tax_amount: Number(header.tax_amount || 0),
    currency: header.currency,
    exchange_rate: Number(header.exchange_rate || 1),
    attachment_url: header.attachment_url || null,
    notes: header.notes || null,
    location: header.location || null,
  });

  const buildItemsPayload = () =>
    lines
      .filter((l) => l.inventory_id)
      .map((l) => ({
        inventory_id: l.inventory_id,
        quantity_ordered: Number(l.quantity_ordered || 0),
        unit_cost: Number(l.unit_cost || 0),
        lot_number: l.lot_number || null,
        expiration_date: l.expiration_date || null,
        notes: l.notes || null,
      }));

  // ---- Submit handlers ----
  const handleSubmit = async (e, alsoReceive = false) => {
    if (e) e.preventDefault();
    setError(null);
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      let purchaseId = id;

      if (!isEdit) {
        // CREATE — branch by selected flow (#130)
        if (flow === 'po') {
          // Flow B: formal PO workflow (no invoice yet)
          const poItems = lines
            .filter((l) => l.inventory_id)
            .map((l) => ({
              inventory_id: l.inventory_id,
              quantity: Number(l.quantity_ordered || 0),
              unit_cost: Number(l.unit_cost || 0),
            }));
          const { data: created, error: createErr } = await createPurchaseOrder({
            supplier_id: header.supplier_id,
            // Sin esto la PO nace con project_id NULL y la aprobación
            // delegada del líder no aplica (ver el select "Proyecto").
            project_id: header.project_id || undefined,
            po_date: header.invoice_date,
            currency: header.currency,
            location: header.location || undefined,
            notes: header.notes || undefined,
            items: poItems,
          });
          if (createErr) {
            throw new Error(createErr.message || 'No se pudo crear la PO');
          }
          purchaseId = created.id;
          toast.success('PO creada como borrador');
        } else {
          // Flow A: invoice-first
          const { data: created, error: createErr } = await createPurchase(
            { ...buildHeaderPayload(), status: header.status },
            buildItemsPayload()
          );
          if (createErr) {
            throw new Error(createErr.message || 'No se pudo crear la compra');
          }
          purchaseId = created.id;
          toast.success('Compra creada');
        }
      } else {
        // UPDATE header
        const { error: updErr } = await updatePurchase(id, buildHeaderPayload());
        if (updErr) throw new Error(updErr.message || 'No se pudo actualizar la compra');

        // Las líneas se guardan una por una. Antes cada fallo era un
        // `console.warn` y el `toast.success('Compra actualizada')` de abajo
        // corría igual: el usuario veía la confirmación verde, navegaba al
        // detalle y las líneas que había agregado no estaban. Ahora se juntan
        // los fallos y, si hay alguno, se reporta como error.
        const lineFailures = [];

        // Delete removed line items
        for (const removedId of removedLineIds) {
          const { error: delErr } = await deletePurchaseItem(removedId);
          if (delErr) lineFailures.push(`No se pudo eliminar una línea: ${delErr.message}`);
        }

        // Add new + update existing
        for (const line of lines) {
          if (!line.inventory_id) continue;
          const payload = {
            inventory_id: line.inventory_id,
            quantity_ordered: Number(line.quantity_ordered || 0),
            unit_cost: Number(line.unit_cost || 0),
            lot_number: line.lot_number || null,
            expiration_date: line.expiration_date || null,
            notes: line.notes || null,
          };
          if (!line.id) {
            const { error: addErr } = await addPurchaseItem(id, payload);
            if (addErr) lineFailures.push(`No se pudo agregar una línea: ${addErr.message}`);
          } else if (line._original) {
            // Compare against original
            const orig = line._original;
            const changed =
              String(orig.inventory_id) !== String(line.inventory_id) ||
              Number(orig.quantity_ordered || 0) !== Number(line.quantity_ordered || 0) ||
              Number(orig.unit_cost || 0) !== Number(line.unit_cost || 0) ||
              (orig.lot_number || '') !== (line.lot_number || '') ||
              (orig.expiration_date || '') !== (line.expiration_date || '') ||
              (orig.notes || '') !== (line.notes || '');
            if (changed) {
              const { error: upErr } = await updatePurchaseItem(line.id, payload);
              if (upErr) lineFailures.push(`No se pudo actualizar una línea: ${upErr.message}`);
            }
          }
        }

        if (lineFailures.length > 0) {
          throw new Error(
            `El encabezado se guardó, pero ${lineFailures.length} ` +
            `${lineFailures.length === 1 ? 'línea falló' : 'líneas fallaron'}: ` +
            lineFailures.join(' · ')
          );
        }

        toast.success('Compra actualizada');
      }

      // Optionally receive full purchase
      if (alsoReceive && purchaseId) {
        const { error: rcvErr } = await receiveFullPurchase(purchaseId);
        if (rcvErr) {
          toast.error('Compra guardada, pero la recepción falló');
        } else {
          toast.success('Compra recibida en su totalidad');
        }
      }

      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      if (purchaseId) {
        queryClient.invalidateQueries({ queryKey: ['purchase', purchaseId] });
      }
      navigate(`/compras/${purchaseId}`);
    } catch (err) {
      console.error('Submit purchase error:', err);
      setError(err.message || 'Error al guardar compra');
      toast.error('Error al guardar compra');
    } finally {
      setSubmitting(false);
    }
  };

  if (isEdit && loadingPurchase) {
    return (
      <Container fluid className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Cargando compra...</p>
      </Container>
    );
  }

  if (isEdit && (loadError || !existingPurchase)) {
    return (
      <Container fluid>
        <Alert variant="danger">
          {loadError?.message || 'No se pudo cargar la compra'}
        </Alert>
        <Button variant="secondary" onClick={() => navigate('/compras')}>
          <i className="bi bi-arrow-left me-2"></i>
          Volver
        </Button>
      </Container>
    );
  }

  // admin-only: purchase form exposes supplier unit costs
  if (!isAdmin) {
    return (
      <Container fluid className="py-5 text-center">
        <Alert variant="warning">
          <i className="bi bi-lock me-2"></i>
          No tienes permiso para gestionar compras.
        </Alert>
        <Button variant="outline-secondary" onClick={() => navigate(-1)}>
          Volver
        </Button>
      </Container>
    );
  }

  return (
    <DashboardLayout>
    <Container fluid className="purchase-form">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2 text-decoration-none"
            onClick={() => navigate('/compras')}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Volver a compras
          </Button>
          <h2 className="mb-1">
            <i className="bi bi-receipt me-2"></i>
            {isEdit ? 'Editar compra' : 'Nueva compra'}
          </h2>
          <p className="text-muted mb-0">
            {isEdit ? 'Modificá el encabezado y las líneas pendientes' : 'Registrá una factura de compra y sus líneas'}
          </p>
        </div>
      </div>

      {/* Flow selector — only on create (#130) */}
      {!isEdit && (
        <Card className="shadow-sm mb-4" data-testid="purchase-flow-selector">
          <Card.Body>
            <div className="mb-2">
              <strong>¿Cómo querés registrar esta compra?</strong>
            </div>
            <div className="d-flex flex-column flex-md-row gap-2">
              <Form.Check
                type="radio"
                id="flow-invoice"
                name="purchase-flow"
                checked={flow === 'invoice'}
                onChange={() => setFlow('invoice')}
                disabled={submitting}
                className="me-3"
                label={
                  <span>
                    <i className="bi bi-receipt me-2 text-primary"></i>
                    <strong>Registrar factura</strong>
                    <small className="d-block text-muted">
                      Ya tengo la factura del proveedor en mano. Crea la compra
                      y opcionalmente recibe inventario de una vez (Flujo A).
                    </small>
                  </span>
                }
              />
              <Form.Check
                type="radio"
                id="flow-po"
                name="purchase-flow"
                checked={flow === 'po'}
                onChange={() => setFlow('po')}
                disabled={submitting}
                label={
                  <span>
                    <i className="bi bi-file-earmark-text me-2 text-success"></i>
                    <strong>Crear PO formal</strong>
                    <small className="d-block text-muted">
                      Aún no tengo factura. Crea una orden de compra como
                      borrador y avanza por el flujo: aprobación → envío →
                      recepción → factura (Flujo B).
                    </small>
                  </span>
                }
              />
            </div>
          </Card.Body>
        </Card>
      )}

      {isLockedForEdit && (
        <Alert variant="warning">
          <i className="bi bi-lock me-2"></i>
          Esta compra ya no se puede editar: está en estado{' '}
          <strong>
            {PURCHASE_STATUS_LABELS[existingPurchase?.status] || existingPurchase?.status}
          </strong>
          . Solo se editan los borradores y las compras recién facturadas.
          Para ver el detalle haz click{' '}
          <Alert.Link onClick={() => navigate(`/compras/${id}`)}>aqui</Alert.Link>.
        </Alert>
      )}

      <Form onSubmit={(e) => handleSubmit(e, false)}>
        <fieldset disabled={isLockedForEdit || submitting}>
          {/* Header */}
          <Card className="shadow-sm mb-4">
            <Card.Header>
              <strong>Datos de la factura</strong>
            </Card.Header>
            <Card.Body>
              <Row className="g-3">
                <Col xs={12} md={6}>
                  <Form.Label>
                    Proveedor <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={header.supplier_id}
                    onChange={(e) => handleHeaderChange('supplier_id', e.target.value)}
                    required
                  >
                    <option value="">Seleccionar...</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label>
                    Número de factura
                    {requiresInvoiceData && <span className="text-danger ms-1">*</span>}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={header.invoice_number}
                    onChange={(e) => handleHeaderChange('invoice_number', e.target.value)}
                    placeholder="Ej. F-1234"
                    maxLength={100}
                  />
                  {requiresInvoiceData && (
                    <Form.Text className="text-muted">
                      Obligatorio en el flujo de factura: la compra nace en
                      estado "Facturada" y la base exige el número.
                    </Form.Text>
                  )}
                </Col>
                <Col xs={12} md={6}>
                  <Form.Label>Proyecto</Form.Label>
                  <Form.Select
                    value={header.project_id}
                    onChange={(e) => handleHeaderChange('project_id', e.target.value)}
                  >
                    <option value="">Sin proyecto (compra de bodega)</option>
                    {projectOptions.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title || p.project_number}
                      </option>
                    ))}
                  </Form.Select>
                  {flow === 'po' && !header.project_id && (
                    <Form.Text className="text-warning">
                      Sin proyecto, esta PO sólo la puede aprobar un
                      administrador: la aprobación delegada se otorga por
                      proyecto.
                    </Form.Text>
                  )}
                </Col>
                <Col xs={12} md={4}>
                  <Form.Label>
                    Fecha de factura <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={header.invoice_date}
                    onChange={(e) => handleHeaderChange('invoice_date', e.target.value)}
                    required
                  />
                </Col>
                <Col xs={12} md={4}>
                  <Form.Label>Ubicación / bodega</Form.Label>
                  <Form.Control
                    type="text"
                    value={header.location}
                    onChange={(e) => handleHeaderChange('location', e.target.value)}
                    placeholder="Opcional"
                    maxLength={100}
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label>Moneda</Form.Label>
                  <Form.Select
                    value={header.currency}
                    onChange={(e) => handleHeaderChange('currency', e.target.value)}
                  >
                    <option value="CRC">CRC</option>
                    <option value="USD">USD</option>
                  </Form.Select>
                </Col>
                <Col xs={6} md={2}>
                  <Form.Label>Tipo de cambio</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.01"
                    value={header.exchange_rate}
                    onChange={(e) => handleHeaderChange('exchange_rate', e.target.value)}
                    disabled={header.currency !== 'USD'}
                  />
                </Col>
                <Col xs={12}>
                  <Form.Label>Notas</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={header.notes}
                    onChange={(e) => handleHeaderChange('notes', e.target.value)}
                    placeholder="Observaciones generales (opcional)"
                    maxLength={1000}
                  />
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Line items */}
          <Card className="shadow-sm mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Lineas</strong>
              {/* En movil el boton de agregar vive al final de las tarjetas
                  (mismo criterio que QuotationBuilder): ahi es donde termina
                  de leerse la ultima linea. */}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={handleAddLine}
                disabled={isLockedForEdit || submitting}
                className="d-none d-md-inline-flex"
              >
                <i className="bi bi-plus-lg me-1"></i>
                Agregar linea
              </Button>
            </Card.Header>
            <Card.Body className="p-0">
              {/* Desktop Table View */}
              <div className="purchase-lines-table-desktop">
                <div className="table-responsive">
                  <Table className="mb-0 align-middle" style={{ minWidth: '900px' }}>
                    <thead className="table-light">
                      <tr>
                        <th>Producto</th>
                        <th>Cant.</th>
                        <th>Costo unit.</th>
                        <th className="text-end">Total</th>
                        <th>Lote</th>
                        <th>Vence</th>
                        <th>Notas</th>
                        <th className="text-end">&nbsp;</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => (
                        <PurchaseLineItem
                          key={line.__key}
                          item={line}
                          index={index}
                          inventoryOptions={inventoryOptions}
                          currency={header.currency}
                          onChange={handleLineChange}
                          onRemove={handleRemoveLine}
                          disabled={isLockedForEdit || submitting}
                        />
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Mobile Cards View */}
              <div className="purchase-lines-cards-mobile">
                {lines.map((line, index) => (
                  <PurchaseLineCard
                    key={line.__key}
                    item={line}
                    index={index}
                    inventoryOptions={inventoryOptions}
                    currency={header.currency}
                    onChange={handleLineChange}
                    onRemove={handleRemoveLine}
                    disabled={isLockedForEdit || submitting}
                  />
                ))}
                <Button
                  variant="outline-primary"
                  className="w-100"
                  onClick={handleAddLine}
                  disabled={isLockedForEdit || submitting}
                >
                  <i className="bi bi-plus-lg me-1"></i>
                  Agregar linea
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Attachment + summary */}
          <Row className="g-3 mb-4">
            <Col xs={12} lg={7}>
              <Card className="shadow-sm h-100">
                <Card.Header>
                  <strong>Comprobante</strong>
                  {requiresInvoiceData && <span className="text-danger ms-1">*</span>}
                </Card.Header>
                <Card.Body>
                  <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                    Si la factura es de papel, tomale una foto con el celular. Si
                    llegó por correo, subí el PDF. Queda guardado en el detalle de
                    la compra.
                  </p>
                  <InvoiceUploader
                    value={header.attachment_url}
                    onChange={(path) => handleHeaderChange('attachment_url', path || '')}
                    purchaseId={id || 'new'}
                    disabled={isLockedForEdit || submitting}
                    required={requiresInvoiceData}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={5}>
              <Card className="shadow-sm h-100">
                <Card.Header>
                  <strong>Resumen</strong>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted">Subtotal</span>
                    <span>{formatCurrency(subtotal, header.currency)}</span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <span className="text-muted">Impuesto</span>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      size="sm"
                      style={{ maxWidth: '140px' }}
                      value={header.tax_amount}
                      onChange={(e) => handleHeaderChange('tax_amount', e.target.value)}
                    />
                  </div>
                  <hr />
                  <div className="d-flex justify-content-between">
                    <strong>Total</strong>
                    <strong className="fs-5">
                      {formatCurrency(total, header.currency)}
                    </strong>
                  </div>
                  {header.currency === 'USD' && totalCRC !== null && (
                    <div className="mt-2 text-end small text-muted">
                      Equivalente: {formatCurrency(totalCRC, 'CRC')}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {error && (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {/* Actions */}
          <div className="d-flex flex-column flex-md-row justify-content-end gap-2">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={() => navigate('/compras')}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitting || isLockedForEdit}
            >
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="bi bi-save me-2"></i>
                  {!isEdit && flow === 'po' ? 'Crear PO (borrador)' : 'Guardar como borrador'}
                </>
              )}
            </Button>
            {/* "Guardar y recibir todo" only makes sense for Flow A (invoice-first) */}
            {(isEdit || flow === 'invoice') && (
              <Button
                type="button"
                variant="success"
                onClick={(e) => handleSubmit(e, true)}
                disabled={submitting || isLockedForEdit}
              >
                {submitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check2-circle me-2"></i>
                    Guardar y recibir todo
                  </>
                )}
              </Button>
            )}
          </div>
        </fieldset>
      </Form>
    </Container>
    </DashboardLayout>
  );
};

export default PurchaseForm;
